import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import styled, { keyframes } from 'styled-components';
import DOMPurify from 'dompurify';
import { postInviteClick } from '../api/videoService';
import { logoBgColor } from '../utils/logoBg';

const API_URL = process.env.REACT_APP_API_URL || 'https://videoreel-api-habwcma9cygwhphm.westus-01.azurewebsites.net';
export const OPEN_INVITE_TOKEN_KEY = 'vusub_pending_open_invite';
const INVITE_CLICK_ID_KEY = 'vusub_invite_click_id';

const fadeIn = keyframes`from { opacity: 0; } to { opacity: 1; }`;
const slideUp = keyframes`from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); }`;

const Page = styled.div`
  min-height: 100vh;
  background: linear-gradient(160deg, #08090f 0%, #0c0e1a 55%, #0f1020 100%);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 40px 20px;
  animation: ${fadeIn} 250ms ease;
`;

const Card = styled.div`
  background: linear-gradient(160deg, #0f1020 0%, #141628 100%);
  border: 1px solid rgba(255,255,255,0.09);
  border-radius: 22px;
  padding: 44px 40px 40px;
  width: 100%;
  max-width: 440px;
  box-shadow: 0 32px 80px rgba(0,0,0,0.65), 0 0 0 1px rgba(101,194,213,0.06);
  animation: ${slideUp} 300ms ease;
  text-align: center;
`;

const Logo = styled.div`
  margin-bottom: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
`;

const LogoImg = styled.img`
  width: 34px;
  height: 34px;
  object-fit: contain;
`;

const LogoText = styled.span`
  font-size: 1.1rem;
  font-weight: 800;
  color: #fff;
  letter-spacing: -0.01em;
`;

const PubLogo = styled.img`
  display: block;
  width: auto;
  height: auto;
  max-width: 240px;
  max-height: 84px;
  object-fit: contain;
  margin: 0 auto 18px;
  padding: 10px 14px;
  border-radius: 14px;
  /* Design 038: background is publisher-chosen (light tile / transparent / dark), so the
     logo suits its own colours on the dark card. Contain (not cover) keeps wide wordmarks
     from being cropped. */
  background: ${p => logoBgColor(p.$bg)};
  border: 1px solid ${p => (p.$bg === 'transparent' ? 'transparent' : 'rgba(255,255,255,0.12)')};
  box-sizing: border-box;
`;

const PubLogoPlaceholder = styled.div`
  width: 72px;
  height: 72px;
  border-radius: 16px;
  background: ${p => p.$color || '#ff4757'}22;
  border: 1px solid ${p => p.$color || '#ff4757'}44;
  margin: 0 auto 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.8rem;
`;

const Title = styled.h1`
  color: white;
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0 0 10px;
`;

const Sub = styled.p`
  color: rgba(255,255,255,0.4);
  font-size: 0.88rem;
  margin: 0 0 32px;
  line-height: 1.6;
`;

const JoinBtn = styled.button`
  width: 100%;
  padding: 14px;
  background: ${p => p.$color || '#E14767'};
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 0.95rem;
  font-weight: 700;
  cursor: pointer;
  transition: opacity 0.2s, transform 0.15s;
  &:hover:not(:disabled) { opacity: 0.88; transform: translateY(-1px); }
  &:disabled { opacity: 0.5; cursor: default; }
`;

// Secondary, visually calmer "Sign in" so it reads clearly as the other path.
const SecondaryBtn = styled.button`
  width: 100%;
  padding: 12px;
  margin-top: 10px;
  background: rgba(255,255,255,0.05);
  color: rgba(255,255,255,0.85);
  border: 1px solid rgba(255,255,255,0.18);
  border-radius: 12px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s, border-color 0.2s;
  &:hover:not(:disabled) { background: rgba(255,255,255,0.1); border-color: rgba(255,255,255,0.35); }
  &:disabled { opacity: 0.5; cursor: default; }
`;

const StatusMsg = styled.p`
  margin: 16px 0 0;
  font-size: 0.85rem;
  color: ${p => p.$err ? '#ff6b7a' : '#2ed573'};
  text-align: center;
`;

const Spinner = styled.div`
  color: rgba(255,255,255,0.4);
  font-size: 0.9rem;
  padding: 20px 0;
  text-align: center;
`;

const InviteMessage = styled.div`
  color: rgba(255,255,255,0.7);
  font-size: 0.92rem;
  line-height: 1.65;
  background: rgba(255,255,255,0.05);
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 12px;
  padding: 14px 18px;
  margin: 0 0 24px;
  text-align: left;
  p { margin: 0 0 0.6em; &:last-child { margin-bottom: 0; } }
  strong { color: rgba(255,255,255,0.9); font-weight: 700; }
  em { font-style: italic; }
  u { text-decoration: underline; }
  ul, ol { margin: 0.4em 0 0.6em 1.4em; padding: 0; }
  li { margin: 0.15em 0; }
  a { color: #65c2d5; text-decoration: none; &:hover { text-decoration: underline; } }
`;

const SubscriberJoinPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { instance, accounts } = useMsal();

  const [pubInfo, setPubInfo] = useState(null);
  const [loadStatus, setLoadStatus] = useState('loading'); // loading | ready | invalid | full
  const [joining, setJoining] = useState(false);
  const [joinError, setJoinError] = useState('');
  const [joined, setJoined] = useState(false);

  // Allow body scroll on this page (body has overflow:hidden globally for the video feed)
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'auto';
    return () => { document.body.style.overflow = prev; };
  }, []);

  // Fire the invite-click beacon once on mount (fire-and-forget; never blocks render).
  // Stash the returned click_id so the join call can mark this click as converted.
  useEffect(() => {
    if (!token) return;
    postInviteClick(token, 'open').then(clickId => {
      if (clickId) {
        try { sessionStorage.setItem(INVITE_CLICK_ID_KEY, clickId); } catch { /* ignore */ }
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Validate the token and load pub info
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/subscriber/open-invite/${encodeURIComponent(token)}`);
        const data = await res.json();
        if (!res.ok) throw new Error(data.detail || 'Invalid invite link');
        setPubInfo(data);
        // Design 037: publication may be at its signup cap — show the closed state.
        setLoadStatus(data.signups_full ? 'full' : 'ready');
      } catch {
        setLoadStatus('invalid');
      }
    })();
  }, [token]);

  // If already authenticated, auto-join
  useEffect(() => {
    if (loadStatus !== 'ready' || !pubInfo || joined) return;
    const account = accounts[0];
    if (!account) return;
    doJoin(account);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadStatus, accounts]);

  const doJoin = async (account) => {
    setJoining(true);
    setJoinError('');
    try {
      let result = await instance.acquireTokenSilent({ scopes: ['openid', 'profile'], account });
      if (!result.idToken) {
        result = await instance.acquireTokenSilent({ scopes: ['openid', 'profile'], account, forceRefresh: true });
      }
      const idToken = result.idToken;
      if (!idToken) throw new Error('Could not get auth token');
      const clickId = sessionStorage.getItem(INVITE_CLICK_ID_KEY);
      const res = await fetch(`${API_URL}/api/subscriber/open-invite/${encodeURIComponent(token)}/join`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
        body: JSON.stringify(clickId ? { click_id: clickId } : {}),
      });
      const data = await res.json();
      if (!res.ok) {
        // Design 037: the cap filled between page-load and join → show the closed state.
        if (data?.detail?.code === 'signups_full') {
          setPubInfo(p => ({ ...(p || {}), full_message: data.detail.message }));
          setLoadStatus('full');
          return;
        }
        // detail may be a dict on other structured errors — only surface a string.
        throw new Error((typeof data.detail === 'string' && data.detail) || `Error ${res.status}`);
      }
      setJoined(true);
      setTimeout(() => navigate(`/feed/${data.publication_id}`, { replace: true }), 1200);
    } catch (e) {
      setJoinError(e.message);
    } finally {
      setJoining(false);
    }
  };

  // Both paths store the token so App.js can complete the join after redirect.
  // `prompt: 'create'` deep-links to the Entra sign-up screen (invitees are new users);
  // sign-in omits it. doJoin runs automatically on return regardless of which was used.
  const startAuth = async (extra) => {
    sessionStorage.setItem(OPEN_INVITE_TOKEN_KEY, token);
    try {
      await instance.loginRedirect({ scopes: ['openid', 'profile'], ...extra });
    } catch {
      // loginRedirect navigates away; if it throws, fall back
      sessionStorage.removeItem(OPEN_INVITE_TOKEN_KEY);
    }
  };

  return (
    <Page>
      <Card>
        <Logo>
          <LogoImg src="/images/vusub-logo.svg" alt="vusub" />
          <LogoText>vusub</LogoText>
        </Logo>

        {loadStatus === 'loading' && <Spinner>Loading…</Spinner>}

        {loadStatus === 'invalid' && (
          <>
            <Title>Link unavailable</Title>
            <Sub>This invite link is invalid or has been revoked. Ask the publisher to share a new link.</Sub>
          </>
        )}

        {loadStatus === 'full' && pubInfo && (
          <>
            {pubInfo.logo_url
              ? <PubLogo src={pubInfo.logo_url} $bg={pubInfo.logo_bg} alt={pubInfo.publication_name} onError={e => { e.target.style.display = 'none'; }} />
              : <PubLogoPlaceholder $color={pubInfo.color}>📰</PubLogoPlaceholder>
            }
            <Title>Signups are closed</Title>
            {/* Design 037: publisher-authored plain-text message — HTML-escape, then \n → <br>. */}
            <InviteMessage
              dangerouslySetInnerHTML={{
                __html: (pubInfo.full_message || "This publication isn't accepting new signups right now.")
                  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
                  .replace(/\n/g, '<br>'),
              }}
            />
          </>
        )}

        {loadStatus === 'ready' && pubInfo && (
          <>
            {pubInfo.logo_url
              ? <PubLogo src={pubInfo.logo_url} $bg={pubInfo.logo_bg} alt={pubInfo.publication_name} onError={e => { e.target.style.display = 'none'; }} />
              : <PubLogoPlaceholder $color={pubInfo.color}>📰</PubLogoPlaceholder>
            }
            <Title>Join {pubInfo.publication_name}</Title>
            <Sub>
              {pubInfo.description || `Sign in to start watching the ${pubInfo.publication_name} reel.`}
            </Sub>
            {pubInfo.invite_message && (() => {
              const msg = pubInfo.invite_message;
              const isHtml = /<[a-z][\s\S]*>/i.test(msg);
              const html = isHtml
                ? DOMPurify.sanitize(msg)
                : msg.replace(/\n/g, '<br>');
              return <InviteMessage dangerouslySetInnerHTML={{ __html: html }} />;
            })()}

            {joined ? (
              <StatusMsg>Joined! Taking you to the feed…</StatusMsg>
            ) : accounts[0] ? (
              <>
                <JoinBtn $color={pubInfo.color} disabled={joining} onClick={() => doJoin(accounts[0])}>
                  {joining ? 'Joining…' : 'Join now'}
                </JoinBtn>
                {joinError && <StatusMsg $err>{joinError}</StatusMsg>}
              </>
            ) : (
              <>
                <JoinBtn $color={pubInfo.color} onClick={() => startAuth({ prompt: 'create' })}>
                  Create account to join
                </JoinBtn>
                <SecondaryBtn onClick={() => startAuth({})}>
                  Already have an account? Sign in
                </SecondaryBtn>
                <Sub style={{ marginTop: 16, marginBottom: 0, fontSize: '0.78rem' }}>
                  You'll be added to {pubInfo.publication_name} automatically once you finish.
                </Sub>
              </>
            )}
          </>
        )}
      </Card>
    </Page>
  );
};

export default SubscriberJoinPage;
